import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileSignature, Upload, Send, Plus, Trash2, X, Check, Clock, AlertTriangle, Copy, ExternalLink, Download, ShieldCheck } from "lucide-react";
import {
  listarDocumentosProjeto,
  criarDocumento,
  enviarParaAssinatura,
  cancelarDocumento,
  listarSignatarios,
  DocumentoAssinatura,
  SignatarioInput,
} from "../../services/assinaturas";
import { anexoUrlAbsoluto } from "../../services/tarefas";
import { useAuth } from "../../hooks/useAuth";
import { useConfirm } from "../../components/ConfirmProvider";

const STATUS_LABEL: Record<string, string> = {
  rascunho: "Rascunho",
  aguardando_assinatura: "Aguardando assinatura",
  assinado: "Assinado",
  recusado: "Recusado",
  cancelado: "Cancelado",
  expirado: "Expirado",
  importado: "Importado (assinado externamente)",
};
const STATUS_COR: Record<string, string> = {
  rascunho: "bg-slate-100 text-slate-700",
  aguardando_assinatura: "bg-amber-100 text-amber-800",
  assinado: "bg-emerald-100 text-emerald-700",
  recusado: "bg-red-100 text-red-700",
  cancelado: "bg-slate-100 text-slate-500",
  expirado: "bg-slate-100 text-slate-500",
  importado: "bg-blue-100 text-blue-700",
};

export function ProjetoAssinaturasSection({ projetoId }: { projetoId: string }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [showSolicitar, setShowSolicitar] = useState(false);
  const [showImportar, setShowImportar] = useState(false);
  const [arquivoArrastado, setArquivoArrastado] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const podeOperar = can(["gestor", "admin"]);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (!podeOperar) return;
    const file = Array.from(e.dataTransfer.files || []).find((f) => f.type === "application/pdf");
    if (!file) {
      alert("Apenas PDFs são aceitos para assinatura.");
      return;
    }
    setArquivoArrastado(file);
    setShowSolicitar(true);
  }

  const { data: documentos = [] } = useQuery({
    queryKey: ["assinaturas", projetoId],
    queryFn: () => listarDocumentosProjeto(projetoId),
  });

  const enviarM = useMutation({
    mutationFn: (id: string) => enviarParaAssinatura(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assinaturas", projetoId] }),
  });
  const cancelarM = useMutation({
    mutationFn: (id: string) => cancelarDocumento(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assinaturas", projetoId] }),
  });

  return (
    <div
      className={`bg-white rounded-xl border p-6 transition-colors ${dragOver ? "border-blue-400 ring-2 ring-blue-200 bg-blue-50/40" : "border-slate-200"}`}
      onDragOver={(e) => { if (podeOperar) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <FileSignature size={16} className="text-blue-600" /> Documentos para assinatura
          {podeOperar && <span className="text-[10px] text-slate-400 font-normal">· arraste um PDF aqui</span>}
        </h3>
        {podeOperar && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowImportar(true)}
              className="text-xs flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded text-slate-700"
            >
              <Upload size={12} /> Anexar PDF já assinado
            </button>
            <button
              onClick={() => setShowSolicitar(true)}
              className="text-xs flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded"
            >
              <Plus size={12} /> Solicitar assinatura
            </button>
          </div>
        )}
      </div>

      {documentos.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-6">Nenhum documento ainda.</p>
      )}

      <div className="space-y-3">
        {documentos.map((d) => (
          <DocumentoCard
            key={d.id}
            d={d}
            podeOperar={podeOperar}
            onEnviar={() => enviarM.mutate(d.id)}
            onCancelar={async () => {
              const ok = await confirm({ title: "Cancelar documento", message: `Cancelar "${d.titulo}"? Os signatários não poderão mais assinar.`, danger: true, confirmText: "Cancelar documento" });
              if (ok) cancelarM.mutate(d.id);
            }}
          />
        ))}
      </div>

      {showSolicitar && podeOperar && (
        <SolicitarModal
          projetoId={projetoId}
          arquivoInicial={arquivoArrastado}
          onClose={() => { setShowSolicitar(false); setArquivoArrastado(null); }}
        />
      )}
      {showImportar && podeOperar && (
        <ImportarModal projetoId={projetoId} onClose={() => setShowImportar(false)} />
      )}
    </div>
  );
}

function DocumentoCard({ d, podeOperar, onEnviar, onCancelar }: {
  d: DocumentoAssinatura;
  podeOperar: boolean;
  onEnviar: () => void;
  onCancelar: () => void;
}) {
  const [showLinks, setShowLinks] = useState(false);
  const url = anexoUrlAbsoluto(d.arquivoFinalUrl || d.arquivoUrl);
  const cor = STATUS_COR[d.status] || "bg-slate-100";
  const totalAssinados = (d.signatarios || []).filter((s) => s.status === "assinado").length;
  const total = (d.signatarios || []).length;

  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 flex items-center gap-2 truncate">
            {d.titulo}
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${cor}`}>
              {STATUS_LABEL[d.status] || d.status}
            </span>
          </p>
          {d.descricao && <p className="text-xs text-slate-500 mt-0.5">{d.descricao}</p>}
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
            <span className="font-mono">Código: {d.codigoVerificacao}</span>
            {!d.importado && <span>{totalAssinados}/{total} assinaram</span>}
            <span className="font-mono text-[10px] text-slate-400">SHA-256: {d.arquivoHash.slice(0, 12)}…</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            <Download size={12} /> {d.arquivoFinalUrl ? "Final" : "Original"}
          </a>
        </div>
      </div>

      {/* Signatários inline */}
      {(d.signatarios || []).length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-2">
          {d.signatarios!.map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-xs">
              {s.status === "assinado" ? <Check size={12} className="text-emerald-600 shrink-0" />
                : s.status === "recusado" ? <X size={12} className="text-red-500 shrink-0" />
                : s.status === "visualizado" ? <Clock size={12} className="text-amber-500 shrink-0" />
                : <Clock size={12} className="text-slate-300 shrink-0" />}
              <span className="font-medium text-slate-700 truncate">{s.nome}</span>
              <span className="text-slate-500 truncate">{s.email}</span>
              <span className="ml-auto text-[10px] uppercase text-slate-400">{s.status}</span>
              {s.assinadoEm && <span className="text-[10px] text-slate-400">{new Date(s.assinadoEm).toLocaleString("pt-BR")}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Ações */}
      {podeOperar && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {d.status === "rascunho" && (
            <button onClick={onEnviar} className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded">
              <Send size={12} /> Enviar para assinatura
            </button>
          )}
          {(d.status === "rascunho" || d.status === "aguardando_assinatura") && (
            <button onClick={onCancelar} className="text-xs flex items-center gap-1 bg-slate-100 hover:bg-red-100 hover:text-red-700 text-slate-600 px-3 py-1.5 rounded">
              <Trash2 size={12} /> Cancelar
            </button>
          )}
          {d.status === "aguardando_assinatura" && (
            <button onClick={() => setShowLinks(!showLinks)} className="text-xs flex items-center gap-1 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded">
              <ExternalLink size={12} /> Ver links de assinatura
            </button>
          )}
          <a
            href={`/verificar/${d.codigoVerificacao}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 ml-auto"
          >
            <ShieldCheck size={12} /> Verificar publicamente
          </a>
        </div>
      )}

      {showLinks && podeOperar && <LinksAssinatura documentoId={d.id} />}
    </div>
  );
}

function LinksAssinatura({ documentoId }: { documentoId: string }) {
  const { data: signatarios = [] } = useQuery({
    queryKey: ["assinatura-signatarios", documentoId],
    queryFn: () => listarSignatarios(documentoId),
  });
  const [copied, setCopied] = useState<string | null>(null);

  return (
    <div className="mt-2 space-y-1 text-[11px] bg-slate-50 rounded p-2">
      {signatarios.map((s) => {
        const url = `${window.location.origin}/assinar/${s.tokenAcesso}`;
        return (
          <div key={s.id} className="flex items-center gap-2">
            <span className="font-medium truncate flex-1">{s.nome}</span>
            <button
              onClick={async () => {
                try { await navigator.clipboard.writeText(url); setCopied(s.id); setTimeout(() => setCopied(null), 1500); } catch {}
              }}
              className="text-slate-500 hover:text-slate-700 flex items-center gap-1"
              title={url}
            >
              {copied === s.id ? <Check size={12} /> : <Copy size={12} />} {copied === s.id ? "copiado" : "copiar link"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ImportarModal({ projetoId, onClose }: { projetoId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");

  const criarM = useMutation({
    mutationFn: () => criarDocumento(projetoId, { file: file!, titulo, descricao, importado: true }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["assinaturas", projetoId] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Anexar contrato já assinado</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">
            Use essa opção quando o contrato já foi assinado fora do sistema (ICP-Brasil, papel, outra plataforma).
            O sistema vai apenas armazenar o PDF, calcular o hash e gerar um código de verificação.
          </p>
          <div>
            <label className="text-xs text-slate-600">Título</label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Contrato de prestação de serviços"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Descrição (opcional)</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">PDF do contrato</label>
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full text-xs flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded text-slate-700"
            >
              <Upload size={12} /> {file ? file.name : "Selecionar arquivo PDF"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          {(criarM.error as any)?.response?.data?.error?.message && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {(criarM.error as any).response.data.error.message}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
          <button
            onClick={() => criarM.mutate()}
            disabled={!file || !titulo || criarM.isPending}
            className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {criarM.isPending ? "Enviando…" : "Anexar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SolicitarModal({ projetoId, onClose, arquivoInicial }: { projetoId: string; onClose: () => void; arquivoInicial?: File | null }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(arquivoInicial || null);
  const [titulo, setTitulo] = useState(arquivoInicial?.name?.replace(/\.pdf$/i, "") || "");
  const [descricao, setDescricao] = useState("");
  const [signatarios, setSignatarios] = useState<SignatarioInput[]>([{ nome: "", email: "" }]);
  const [textoConsentimento, setTextoConsentimento] = useState("Li o documento e aceito assiná-lo eletronicamente. Reconheço que esta assinatura tem validade jurídica nos termos da Lei 14.063/2020 e MP 2.200-2/2001.");

  const criarM = useMutation({
    mutationFn: () => criarDocumento(projetoId, {
      file: file!, titulo, descricao,
      textoConsentimento,
      signatarios: signatarios.filter((s) => s.nome && s.email),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["assinaturas", projetoId] }); onClose(); },
  });

  function addSig() {
    setSignatarios([...signatarios, { nome: "", email: "" }]);
  }
  function rmSig(idx: number) {
    setSignatarios(signatarios.filter((_, i) => i !== idx));
  }
  function setSig(idx: number, dados: Partial<SignatarioInput>) {
    setSignatarios(signatarios.map((s, i) => i === idx ? { ...s, ...dados } : s));
  }

  const podeSalvar = file && titulo && signatarios.some((s) => s.nome && s.email);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Solicitar assinatura</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs text-slate-600">PDF do documento</label>
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full text-xs flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded text-slate-700"
            >
              <Upload size={12} /> {file ? file.name : "Selecionar PDF"}
            </button>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div>
            <label className="text-xs text-slate-600">Título</label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Descrição</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-slate-600">Signatários</label>
              <button onClick={addSig} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"><Plus size={12} /> Adicionar</button>
            </div>
            <div className="space-y-2">
              {signatarios.map((s, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-2 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Nome"
                      value={s.nome}
                      onChange={(e) => setSig(idx, { nome: e.target.value })}
                      className="flex-1 px-2 py-1 rounded border border-slate-300 outline-none text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Papel (opcional)"
                      value={s.papel || ""}
                      onChange={(e) => setSig(idx, { papel: e.target.value })}
                      className="w-32 px-2 py-1 rounded border border-slate-300 outline-none text-xs"
                    />
                    {signatarios.length > 1 && (
                      <button onClick={() => rmSig(idx)} className="text-slate-400 hover:text-red-500"><Trash2 size={12} /></button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      placeholder="E-mail"
                      value={s.email}
                      onChange={(e) => setSig(idx, { email: e.target.value })}
                      className="flex-1 px-2 py-1 rounded border border-slate-300 outline-none text-xs"
                    />
                    <input
                      type="text"
                      placeholder="CPF/CNPJ (opcional)"
                      value={s.documento || ""}
                      onChange={(e) => setSig(idx, { documento: e.target.value })}
                      className="w-40 px-2 py-1 rounded border border-slate-300 outline-none text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">
              Cada signatário receberá um link único para assinar. Em LGPD, essa coleta tem como base legal o seu interesse legítimo na execução do contrato.
            </p>
          </div>
          <div>
            <label className="text-xs text-slate-600">Texto de consentimento (mostrado ao signatário)</label>
            <textarea
              value={textoConsentimento}
              onChange={(e) => setTextoConsentimento(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-xs focus:border-blue-500"
            />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800 flex items-start gap-1.5">
            <AlertTriangle size={12} className="shrink-0 mt-0.5" />
            <span>Após criar o documento, ele fica como rascunho. Revise e clique em "Enviar para assinatura" para gerar os links.</span>
          </div>
          {(criarM.error as any)?.response?.data?.error?.message && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {(criarM.error as any).response.data.error.message}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
          <button
            onClick={() => criarM.mutate()}
            disabled={!podeSalvar || criarM.isPending}
            className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {criarM.isPending ? "Criando…" : "Criar rascunho"}
          </button>
        </div>
      </div>
    </div>
  );
}
